import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * M17 — **les listes sont du code, les lignes sont des données.** Une liste nomme un écran de
 * saisie et une colonne du constructeur d'anomalies : en ajouter une est un changement de
 * logiciel, pas d'administration. Les valeurs qu'elle contient, elles, s'administrent depuis
 * « Administration → Règles anomalies ».
 */
export const VOCABULARY_LISTS = [
    'origin',
    'provenance',
    'evacuation',
    'destination',
    'technical',
    'area',
    'fixture',
    'fixture_location',
    'outdoor_element',
    'outdoor_destination',
    'diagram_element',
];
export const vocabularyListSchema = z.enum(VOCABULARY_LISTS);
/**
 * Les deux gravités du modèle. `bad` et `warn` sont ce dont l'avis de conformité se dérive
 * (M19) : en ajouter une troisième fausserait la conclusion sans que rien ne le signale.
 */
export const severitySchema = z.enum(['bad', 'warn']);
/** Le libellé de chaque gravité, aux deux fronts : celui du prototype (« Règles anomalies »). */
export const SEVERITY_LABELS = {
    bad: 'Non conforme',
    warn: 'À contrôler',
};
/**
 * M17 — une entrée de référentiel.
 *
 * `code` est la **clé stable** que les règles (M18) et les anomalies gelées (M19) citent ;
 * `label` est du texte client, reformulable sans migrer une donnée. La contrainte utile est
 * `UNIQUE (list, code)` — c'est elle qui règle « Non raccordé », présent dans deux listes avec
 * deux gravités : deux lignes, la sévérité appartenant au **couple**.
 *
 * `active` plutôt qu'une suppression : une valeur retirée reste citée par des règles et par des
 * anomalies archivées. On la sort des écrans de saisie, on ne casse pas ce qui la référence.
 */
export const vocabularyEntrySchema = z.object({
    id: uuidV7Schema,
    list: vocabularyListSchema,
    code: z.string().min(1),
    label: z.string().min(1),
    severity: severitySchema.nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    /**
     * Les travaux que la valeur propose pour « Travaux à réaliser », une phrase par ligne (M17).
     * Absente d'une API d'avant la colonne, elle se lit « aucuns travaux » plutôt que de refuser le
     * référentiel entier.
     */
    recommendation: z.string().nullable().default(null),
});
/**
 * Ce qu'une phrase proposée écrit à la place des origines de l'anomalie — « Raccorder {origine}
 * au réseau d'eaux usées ou condamner ». En français : l'admin de GHP le lit et le tape (M17).
 */
export const RECOMMENDATION_ORIGIN_PLACEHOLDER = '{origine}';
/**
 * Les deux seules listes dont une entrée porte une gravité (M17). Ailleurs, `severity` reste
 * nulle : une origine ou une provenance ne sont ni conformes ni non conformes, c'est la
 * **destination constatée** qui l'est, et l'élément technique qui l'est par lui-même.
 *
 * ⚠️ **Plus strict que `vocabularyEntrySchema`, exprès.** La lecture doit pouvoir relire ce que
 * la base porte déjà — une ligne seedée de travers se relit et se corrige ; l'écriture, elle,
 * n'a aucune raison de laisser en poser une nouvelle. Une gravité sur une origine ne serait pas
 * une saisie douteuse à enregistrer (M21) mais une valeur que l'évaluateur (M18) ne lirait
 * jamais : elle se verrait à l'écran sans rien changer au verdict.
 */
export const LISTS_WITH_SEVERITY = ['destination', 'technical'];
const carriesSeverity = (list) => LISTS_WITH_SEVERITY.includes(list);
/**
 * La forme d'un code de référentiel : capitales ASCII, chiffres et `_`, une lettre en tête.
 *
 * C'est ce que la base porte déjà (`DEST_EP`, `VERS_FOSSE_OUVERT`, `MAL`) et ce que la syntaxe
 * texte des règles (M18) sait citer sans guillemets. **Une lettre en tête** parce qu'un code
 * commençant par un chiffre se lirait comme un nombre dans cette syntaxe — c'est la raison pour
 * laquelle le seed écrit `DEUX_BONDES_DE_SOL` et non `2_BONDES_DE_SOL`.
 */
export const vocabularyCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_]*$/, {
    error: 'un code s’écrit en capitales sans accent, chiffres et « _ » admis, une lettre en tête',
});
/**
 * Le code que l'administration **se voit proposer** à partir du libellé — une proposition, jamais
 * une dérivation appliquée dans son dos.
 *
 * ⚠️ **C'est tout l'intérêt qu'elle reste corrigeable**, et le seed de référence le prouve trois
 * fois : « Lave-linge » se code `MAL` (le sigle de la fiche terrain réelle), « Vers fossé » et
 * « Vers fosse » sont deux valeurs distinctes que la chute des accents confondrait en un
 * `VERS_FOSSE` piégeux, et « 2 Bondes de sol » n'a pas de code dérivable du tout. Une dérivation
 * imposée aurait produit les trois erreurs sans que rien ne le signale.
 *
 * `null` quand aucun code valide ne sort du libellé — un libellé vide, uniquement ponctué, ou
 * commençant par un chiffre. L'appelant demande alors le code à l'humain, comme l'auteur du seed
 * l'a fait.
 */
export function suggestVocabularyCode(label) {
    const suggestion = label
        .normalize('NFD')
        // La plage Unicode des diacritiques combinants : « Évier » devient « EVIER », pas « VIER ».
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return vocabularyCodeSchema.safeParse(suggestion).success ? suggestion : null;
}
/**
 * Ce que l'administration fournit pour ajouter une valeur au référentiel (M17).
 *
 * Ce que ce schéma ne porte pas est aussi décidé que ce qu'il porte :
 *
 * - **`id`** — une PK se génère côté serveur ici, contrairement aux entités que la tablette crée
 *   hors réseau (M7) : l'écran n'a rien à rejouer, il est en ligne quand il l'ajoute.
 * - **`sortOrder`** — la valeur s'ajoute **en fin de liste**, comme dans le prototype validé, qui
 *   n'offre aucun glisser-déposer. Le rang se calcule donc, il ne se fournit pas.
 * - **`active`** — une valeur naît proposable. La désactiver est un acte à part, comme la
 *   fermeture d'un compte (M6).
 *
 * ⚠️ **`strictObject`** : le `strip` par défaut avalerait un `active: false` de trop en silence,
 * et l'appelant croirait avoir créé une valeur déjà retirée.
 */
export const createVocabularyEntrySchema = z
    .strictObject({
    list: vocabularyListSchema,
    code: vocabularyCodeSchema,
    label: z.string().trim().min(1),
    severity: severitySchema.nullable().default(null),
})
    .refine((entry) => entry.severity === null || carriesSeverity(entry.list), {
    error: 'seules les destinations et les éléments techniques portent une gravité',
    path: ['severity'],
})
    /**
     * ⚠️ **Le refus est symétrique, et il manquait** (revue de #183). Le `refine` ci-dessus
     * n'interdisait qu'une gravité **de trop** ; rien n'en exigeait une, et `severity` a
     * `.default(null)` — `POST /vocabulary {"list":"destination","code":"…","label":"…"}` passait
     * donc en `201` avec `severity: null`, vérifié sur l'API réelle.
     *
     * C'est exactement l'état que cette fiche dit prévenir : une destination sans gravité est
     * **invisible pour l'évaluateur**, et le seed n'a aucune ligne pareille sur ses 26. L'écran le
     * garantissait déjà ; le **contrat** ne le garantissait pas, et c'est lui qui tient quand on
     * appelle l'API directement.
     */
    .refine((entry) => entry.severity !== null || !carriesSeverity(entry.list), {
    error: 'une destination ou un élément technique porte toujours une gravité',
    path: ['severity'],
});
/**
 * Ce qui se **corrige** sur une valeur existante (M17) : son libellé, sa gravité là où elle en
 * a une, et les travaux qu'elle propose — un texte vidé les retire.
 *
 * - **`code`** n'y entre pas — c'est la clé stable que les règles (M18) et les anomalies gelées
 *   (M19) citent. Le renommer réécrirait leur sens sans toucher à leur texte. Renommer le
 *   **libellé**, lui, est précisément ce que la séparation code/libellé rend gratuit.
 * - **`list`** non plus : déplacer une valeur d'une liste à l'autre, c'est en retirer une et en
 *   poser une autre, avec la gravité et les règles qui vont avec.
 * - **`active`** non plus, pour la raison de `updateUserSchema` : entrée ici, elle deviendrait un
 *   champ parmi d'autres, basculé par un formulaire qui renvoie tout son état.
 *
 * ⚠️ **La gravité n'est vérifiée contre la liste qu'au service** : un correctif ne porte pas la
 * liste de la ligne qu'il corrige, et l'exiger reviendrait à faire confiance à l'appelant sur la
 * donnée même qui décide du refus.
 *
 * ⚠️ **Au moins un champ** — un `PATCH` vide ne demande rien, et Drizzle refuse un `set({})`.
 */
export const updateVocabularyEntrySchema = z
    .strictObject({
    label: z.string().trim().min(1).optional(),
    severity: severitySchema.nullable().optional(),
    recommendation: z
        .string()
        .trim()
        .transform((text) => (text === '' ? null : text))
        .nullable()
        .optional(),
})
    .refine((patch) => Object.keys(patch).length > 0, { error: 'aucun champ à modifier' });
//# sourceMappingURL=vocabulary.js.map